import { ComponentPropsWithoutRef, useMemo } from "react";
import { today } from "@internationalized/date";
import { Link } from "@remix-run/react";

import { useTimetableContext } from "./Timetable.hooks";

import { useSendCAEvent } from "~/routes/api+/enterprises.$enterprise.session-events";
import { isBefore } from "~/utils/date";

interface ChangeWeekLinkProps extends Omit<ComponentPropsWithoutRef<typeof Link>, "to"> {
  type: "previous" | "next";
  "aria-label": string;
}

export function ChangeWeekLink({ type, ...props }: ChangeWeekLinkProps) {
  const ctx = useTimetableContext();
  const sendCAEvent = useSendCAEvent();

  const { nextStartDate, redirectParams } = useMemo(() => {
    if (type === "previous") {
      const nextEndDate = ctx.end.subtract({ weeks: 1 });
      const todayDate = today(ctx.timeZone);

      if (isBefore(nextEndDate, todayDate)) {
        return { redirectParams: null };
      }
    }

    if (type === "next" && ctx.errors.OUT_OF_RANGE) {
      return { redirectParams: null };
    }

    const nextStartDate =
      type === "previous" ? ctx.start.subtract({ weeks: 1 }) : ctx.start.add({ weeks: 1 });

    const searchParams = new URLSearchParams();
    searchParams.set("startDate", nextStartDate.toString());
    searchParams.set("prefetchId", ctx.prefetchId);

    return { redirectParams: searchParams, nextStartDate };
  }, [type, ctx]);

  if (redirectParams === null) {
    return (
      <span
        className="flex cursor-not-allowed items-center font-semibold opacity-50"
        data-component="TimetableChangeWeekLink"
        data-disabled="true"
      >
        {props.children}
      </span>
    );
  }

  return (
    <Link
      className="flex items-center font-semibold hover:text-brand focus:text-brand"
      data-component="TimetableChangeWeekLink"
      onClick={() => {
        if (!nextStartDate) return;

        sendCAEvent({
          type: "timetable-navigation",
          payload: {
            type,
            startDay: nextStartDate.toString(),
            endDay: nextStartDate.add({ weeks: 1 }).toString(),
            timeZone: ctx.timeZone,
          },
        });
      }}
      prefetch={type === "next" ? "render" : "none"}
      to={`?${redirectParams}`}
      replace
      {...props}
    />
  );
}
